import React, { useState, useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HeaderContainer,
  HeaderIconContainerMobileGrid,
  HeaderIconContainerMobile,
  HeaderIconsContainer,
  HeaderIconsLinksContainer,
  HeaderLinksContainer,
  HeaderLockupImg,
  MobileMenuToggleIcon,
} from "./Header.styles";
import unityLockup from "../../assets/logos/unity-rebrand/unity-horiz-cobranded-tint.svg";
import menuExpand from "../../assets/icons/menu.svg";
import close from "../../assets/icons/close.svg";
import { MobileMenu } from "../../MobileMenu/MobileMenu";
import { GetHeaderMenuLinks, GetLoginHeader } from "./HeaderMenuLinks";
import { LANDING_PAGE_URL, LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  getSelectedRequisition,
  getUser,
  getUserAuth,
} from "../../recoil/selectors";
import { patientProfileState, patientState } from "../../recoil/atoms";
import { showUpfrontPayment } from "../UpfrontPayment/Services/UpfrontPaymentService";

export const Header = (): React.ReactElement => {
  const [activeHeader, setActiveHeader] = useState("");
  const [patientInfo, setPatientInfo] = useRecoilState(patientState);
  const [profileInfo, setProfileInfo] = useRecoilState(patientProfileState);

  const userAuth = useRecoilValue(getUserAuth);
  const user = useRecoilValue(getUser);
  const history = useHistory();
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const { isDesktop, deviceType } = useDeviceHook();
  const specificRequisition = useRecoilValue(getSelectedRequisition);

  useEffect(() => {
    setActiveHeader(pathname);
  }, []);

  const setPathnameCheck = () => {
    setActiveHeader(history.location.pathname);
  };

  useEffect(() => {
    const unlisten = history.listen(() => {
      setPathnameCheck();
    });
    return () => {
      unlisten();
    };
  }, []);

  const babyPeekReq = specificRequisition;
  const babyPeekStatus = specificRequisition?.reveal_status;

  const headerLinks = useMemo(
    () =>
      user?.is_verified_email
        ? GetHeaderMenuLinks(
            "white",
            "medium",
            activeHeader,
            setActiveHeader,
            profileInfo,
            setPatientInfo,
            setProfileInfo,
            deviceType,
            patientInfo?.requisitions?.filter((req) =>
              showUpfrontPayment(req.portal_bill_status),
            ),
            babyPeekReq && {
              status: babyPeekStatus,
              babyPeekPK: babyPeekReq.baby_peek_pk,
            },
          )
        : GetLoginHeader(
            "white",
            "medium",
            setPatientInfo,
            pathname === LOGIN_PAGE_URL,
          ),
    [
      pathname,
      user?.is_verified_email,
      profileInfo,
      babyPeekStatus,
      activeHeader,
      deviceType,
      i18n.language,
    ],
  );

  const [navBarOpen, setNavBarOpen] = useState(false);

  const handleToggle = () => {
    setNavBarOpen(!navBarOpen);
  };

  return (
    <>
      {isDesktop && (
        <HeaderContainer data-testid="app-header">
          <HeaderIconsLinksContainer>
            <HeaderIconsContainer
              data-testid="HeaderIcons"
              onClick={() => history.push(LANDING_PAGE_URL)}
            >
              {import.meta.env.VITE_IS_USER_TESTING_ENV !== "true" && (
                <HeaderLockupImg
                  src={unityLockup}
                  alt="Unity by BillionToOne"
                />
              )}
            </HeaderIconsContainer>
            <HeaderLinksContainer onlyLogin={!user?.is_verified_email}>
              {headerLinks
                .filter((header) => !header.hide)
                .map((headerLink) => (
                  <React.Fragment key={headerLink.key}>
                    {headerLink.header}
                  </React.Fragment>
                ))}
            </HeaderLinksContainer>
          </HeaderIconsLinksContainer>
        </HeaderContainer>
      )}
      {!isDesktop && (
        <>
          <HeaderContainer data-testid="app-header">
            <HeaderIconContainerMobile>
              <HeaderIconsContainer
                onClick={() => history.push(LANDING_PAGE_URL)}
              >
                <HeaderLockupImg
                  src={unityLockup}
                  alt="Unity by BillionToOne"
                />
              </HeaderIconsContainer>
              {pathname !== LOGIN_PAGE_URL && (
                <HeaderIconContainerMobileGrid deviceType={deviceType}>
                  <MobileMenuToggleIcon
                    data-testid="hamburger-menu"
                    src={navBarOpen ? close : menuExpand}
                    width="20px"
                    onClick={handleToggle}
                  />
                </HeaderIconContainerMobileGrid>
              )}
            </HeaderIconContainerMobile>
          </HeaderContainer>
          {navBarOpen && <MobileMenu handleToggle={handleToggle} />}
        </>
      )}
    </>
  );
};
